/*
 * Styled-text authoring for set_text `segments` — pure planning (no Office.js).
 *
 * A template box often mixes a styled header run (bold brand-blue "Background")
 * with body runs (regular black) in ONE placeholder. Writing the whole
 * textRange.text collapses it to one font. `segments` lets the agent AUTHOR new
 * content run-by-run: we concatenate the segment texts into the string to write,
 * and return the char range of each segment so the interpreter can style each
 * range in place via TextRange.getSubstring — reproducing the header/body logic
 * on new content (works on a blank style deck too, not just a precedent swap).
 */

export interface TextSegment {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string; // hex, with or without leading '#'
  size?: number; // points
  font?: string; // font family name
}

export interface SegmentRun {
  start: number;
  length: number;
  seg: TextSegment;
}

export interface SegmentPlan {
  fullText: string;
  runs: SegmentRun[];
}

/**
 * Concatenate segment texts and record each segment's [start, length) range in
 * the resulting string. Empty-text segments contribute nothing and produce no
 * run (there is nothing to style). Paragraph/line breaks are whatever the caller
 * puts in `text` (e.g. "Background\n").
 */
export function planSegments(segments: TextSegment[]): SegmentPlan {
  let fullText = "";
  const runs: SegmentRun[] = [];
  for (const seg of segments) {
    const t = seg && seg.text != null ? String(seg.text) : "";
    if (t.length > 0) {
      runs.push({ start: fullText.length, length: t.length, seg });
    }
    fullText += t;
  }
  return { fullText, runs };
}
